import React, { useEffect, useState, useRef } from "react";
import { httpclient } from "../../../utils/httpClient";
import {
  CircularProgress,
  styled,
  Snackbar,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import LoadingScreen from "../../../Components/LoadingScreen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MuiAlert from "@mui/material/Alert";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const FlexContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  fontSize: "17px",
  marginBottom: "10px",
  alignItems: "flex-start",
}));

const FlexInnerTitle = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minWidth: "280px",
  maxWidth: "320px",
}));

const Values = styled("div")(({ theme }) => ({
  marginRight: "15px",
  fontWeight: "500",
  color: theme.palette.primary.dark,
  "& input": {
    padding: "10px",
    width: "100px",
  },
  "& .MuiSelect-select": {
    padding: "10px",
  },
  "& button": {
    padding: "11px !important",
    minWidth: "40px",
  },
}));

const PL = () => {
  const [syncLoading, setSyncLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [syncList, setSyncList] = useState([]);
  const [newSyncList, setNewSyncList] = useState([]);
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messageState, setMessageState] = useState("");
  const [slotDate, setSlotDate] = useState("");
  const [dateError, setDateError] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const dateInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    httpclient.get(`warehouse-setting`).then(({ data }) => {
      if (data.success) {
        setSyncLoading(false);
        setSyncList(data.data);

        // Extract initial slot_date from API
        const firstBranchWithBatch = data.data.find((branch) =>
          branch.settings.some((setting) => 
            setting.batch_setting_enabled === "1" && setting.batch_setting
          )
        );

        if (firstBranchWithBatch) {
          const batchSetting = firstBranchWithBatch.settings.find(
            (s) => s.batch_setting_enabled === "1" && s.batch_setting
          );

          if (batchSetting && batchSetting.batch_setting) {
            const firstSlotWithDate = batchSetting.batch_setting
              .flatMap((m) => m.slots)
              .find((s) => s.slot_date);

            // Only set slotDate if we found a valid slot_date
            if (firstSlotWithDate) {
              setSlotDate(firstSlotWithDate.slot_date);
            } else {
              setSlotDate(""); // Explicitly clear when no slot_date is found
            }
          } else {
            setSlotDate(""); // Clear if no batch_setting found
          }
        } else {
          setSlotDate(""); // Clear if no branch with batch_setting found
        }
      } else {
        setSyncLoading(false);
      }
    });
  };

  useEffect(() => {
    if (syncList.length > 0) setNewSyncList(syncList);
  }, [syncList]);

  const handleChangeAccordion = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : null);
  };

  const handleSettingChange = (branchIndex, settingIndex, newValue) => {
    const newSyncLists = [...newSyncList];
    const setting = newSyncLists[branchIndex].settings[settingIndex];
    setting.value = newValue;

    // Clear date error if setting is unchecked
    if (setting.batch_setting_enabled === "1" && newValue === "0") {
      setDateError(false);
    }

    if (setting.child_setting && newValue === "0") {
      setting.child_setting.forEach(
        (childSetting) => (childSetting.value = "0")
      );
    }
    // Only reset batch settings if batch_setting_enabled is "1"
    if (setting.batch_setting_enabled === "1" && newValue === "0") {
      setting.batch_setting.forEach((marketplace) => {
        marketplace.slots.forEach((slot) => (slot.status = 0));
      });
    }
    setNewSyncList(newSyncLists);
  };

  const handleChildSettingChange = (
    branchIndex,
    parentIndex,
    childIndex,
    newValue
  ) => {
    const newSyncLists = [...newSyncList];
    newSyncLists[branchIndex].settings[parentIndex].child_setting[
      childIndex
    ].value = newValue;
    setNewSyncList(newSyncLists);
  };

  const handleBatchSettingChange = (
    branchIndex,
    settingIndex,
    marketplaceIndex,
    slotIndex
  ) => {
    const newSyncLists = [...newSyncList];
    const setting = newSyncLists[branchIndex].settings[settingIndex];
    if (setting.batch_setting && setting.batch_setting[marketplaceIndex]) {
      setting.batch_setting[marketplaceIndex].slots.forEach(
        (slot) => (slot.status = 0)
      );
      setting.batch_setting[marketplaceIndex].slots[slotIndex].status = 1;
      setNewSyncList(newSyncLists);
    }
  };

  const handleBatchSettingChangeNone = (
    branchIndex,
    settingIndex,
    marketplaceIndex
  ) => {
    const newSyncLists = [...newSyncList];
    const setting = newSyncLists[branchIndex].settings[settingIndex];
    if (setting.batch_setting && setting.batch_setting[marketplaceIndex]) {
      setting.batch_setting[marketplaceIndex].slots.forEach(
        (slot) => (slot.status = 0)
      );
      setNewSyncList(newSyncLists);
    }
  };

  const handleSlotDateChange = (date) => {
    setSlotDate(date);
    setDateError(false); // Clear error when date is selected

    // Update all slots in all branches and marketplaces with the new date
    const newSyncLists = [...newSyncList];
    newSyncLists.forEach(branch => {
      branch.settings.forEach(setting => {
        // Only update settings with batch_setting_enabled === "1"
        if (setting.batch_setting_enabled === "1" && setting.batch_setting) {
          setting.batch_setting.forEach(marketplace => {
            marketplace.slots.forEach(slot => {
              slot.slot_date = date;
            });
          });
        }
      });
    });

    setNewSyncList(newSyncLists);
  };

  const renderSettingField = (setting, branchIndex, settingIndex) => {
    switch (setting.type) {
      case "checkbox":
        return (
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={setting.value === "1"}
                  onChange={(e) =>
                    handleSettingChange(
                      branchIndex,
                      settingIndex,
                      e.target.checked ? "1" : "0"
                    )
                  }
                />
              }
              label={setting.setting_name}
            />
            {setting.child_setting && setting.value === "1" && (
              <div style={{ marginLeft: 30 }}>
                {setting.child_setting.map((childSetting, childIndex) => (
                  <FormControlLabel
                    key={childIndex}
                    control={
                      <Checkbox
                        checked={childSetting.value === "1"}
                        onChange={(e) =>
                          handleChildSettingChange(
                            branchIndex,
                            settingIndex,
                            childIndex,
                            e.target.checked ? "1" : "0"
                          )
                        }
                      />
                    }
                    label={childSetting.setting_name}
                  />
                ))}
              </div>
            )}
  
            {setting.batch_setting_enabled === "1" && setting.batch_setting && (
              <div style={{ marginLeft: 30, marginTop: 15 }}>
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: "bold", marginBottom: 10 }}
                >
                  Batch Settings
                </Typography>

                <div
                  style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
                  onClick={() => dateInputRef.current && dateInputRef.current.click()}
                >
                  <TextField
                    ref={dateInputRef}
                    label="Batch Slot Date"
                    type="date"
                    variant="outlined"
                    value={slotDate || ""}
                    onChange={(e) => handleSlotDateChange(e.target.value)}
                    error={dateError}
                    helperText={dateError ? "Please select a date" : ""}
                    InputProps={{
                      inputProps: {
                        min: new Date().toISOString().split("T")[0],
                        onKeyDown: (e) => e.preventDefault(), // prevent typing
                        placeholder: "", // Remove placeholder text
                        style: { cursor: 'pointer' },
                      },
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon />
                        </InputAdornment>
                      ),
                      style: { cursor: 'pointer' },
                    }}
                    sx={{
                      mb: 3,
                      minWidth: 220,
                      '& .MuiOutlinedInput-root': {
                        cursor: 'pointer',
                      },
                      '& .MuiInputLabel-root': {
                        cursor: 'pointer',
                      }
                    }}
                  />
                </div>

                {setting.batch_setting.map((marketplace, marketplaceIndex) => {
                  const selectedSlotObj = marketplace.slots.find(slot => slot.status === 1);
                  let nextSlotObj = null;
                  let isLastSlot = false;

                  if (selectedSlotObj) {
                    const selectedIndex = marketplace.slots.findIndex(slot => slot.slot === selectedSlotObj.slot);
                    isLastSlot = selectedIndex === marketplace.slots.length - 1;
                    if (selectedIndex < marketplace.slots.length - 1) {
                      // If not the last slot, get the next slot
                      nextSlotObj = marketplace.slots[selectedIndex + 1];
                    } else {
                      // If the last slot, get the first slot for "next day"
                      nextSlotObj = marketplace.slots[0];
                    }
                  }

                  return (
                    <div
                      key={marketplace.marketplace_batch_id}
                      style={{ marginBottom: 20 }}
                    >
                      <Typography
                        variant="body1"
                        style={{ fontWeight: 500, marginBottom: 8 }}
                      >
                        {marketplace.marketplace}
                      </Typography>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FormControl variant="outlined" sx={{ minWidth: 180 }}>
                          <Select
                            displayEmpty
                            value={
                              selectedSlotObj
                                ? selectedSlotObj.slot.toString()
                                : ""
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              const slotIndex = marketplace.slots.findIndex(
                                (slot) => slot.slot.toString() === value
                              );
                              if (slotIndex !== -1) {
                                handleBatchSettingChange(
                                  branchIndex,
                                  settingIndex,
                                  marketplaceIndex,
                                  slotIndex
                                );
                              }
                            }}
                            input={
                              <OutlinedInput
                                notched={false}
                                endAdornment={
                                  selectedSlotObj ? (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() =>
                                          handleBatchSettingChangeNone(
                                            branchIndex,
                                            settingIndex,
                                            marketplaceIndex
                                          )
                                        }
                                        size="small"
                                        edge="end"
                                      >
                                        <ClearIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ) : null
                                }
                              />
                            }
                            IconComponent={() => null}
                            renderValue={(selected) =>
                              selectedSlotObj
                                ? selectedSlotObj.value
                                : "Select Time Slot"
                            }
                          >
                            {marketplace.slots.map((slot) => (
                              <MenuItem
                                key={slot.slot}
                                value={slot.slot.toString()}
                              >
                                {slot.value}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* New message display */}
                        {selectedSlotObj && nextSlotObj && (
                          <Typography variant="body2" style={{ marginLeft: 10, flex: 1 }}>
                            {isLastSlot
                              ? `Orders received between ${selectedSlotObj.value} and ${nextSlotObj.value} batch will be sent next day at ${nextSlotObj.value}`
                              : `Orders received between ${selectedSlotObj.value} and ${nextSlotObj.value} batch will be sent at ${nextSlotObj.value}`}
                          </Typography>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      case "text":
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FlexContent>
                <Values>
                  <TextField
                    value={setting.value}
                    variant="standard"
                    fullWidth
                    onChange={(e) =>
                      handleSettingChange(
                        branchIndex,
                        settingIndex,
                        e.target.value
                      )
                    }
                  />
                </Values>
                <FlexInnerTitle>
                  <span>{setting.setting_name}</span>
                </FlexInnerTitle>
              </FlexContent>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  const validateBeforeSubmit = () => {
    // Check if any branch has batch setting enabled but no date selected
    for (const branch of newSyncList) {
      for (const setting of branch.settings) {
        // Only validate settings with batch_setting_enabled === "1"
        if (setting.batch_setting_enabled === "1" && setting.batch_setting) {
          if (!slotDate) {
            setDateError(true);
            setMessage("Please select a date for batch settings");
            setMessageState("error");
            setOpen(true);
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleSubmit = () => {
    // Validate before submitting
    if (!validateBeforeSubmit()) {
      return;
    }

    // Show confirmation dialog instead of directly submitting
    setConfirmDialogOpen(true);
  };

  const handleConfirmSubmit = () => {
    // Close the confirmation dialog
    setConfirmDialogOpen(false);
    
    setLoading(true);

    // Create a deep copy of newSyncList to update all slot_date values
    const updatedSyncList = JSON.parse(JSON.stringify(newSyncList));

    // Update all slots in all branches with the current slotDate value
    updatedSyncList.forEach(branch => {
      branch.settings.forEach(setting => {
        // Only update settings with batch_setting_enabled === "1"
        if (setting.batch_setting_enabled === "1" && setting.batch_setting) {
          setting.batch_setting.forEach(marketplace => {
            marketplace.slots.forEach(slot => {
              slot.slot_date = slotDate;
            });
          });
        }
      });
    });

    httpclient.post(`warehouse-setting`, updatedSyncList).then(({ data }) => {
      setLoading(false);
      if (data.success) {
        setOpen(true);
        setMessage(data.message);
        setMessageState("success");
        setExpandedAccordion(null);
        // Refresh data after successful update
        fetchData();
      } else {
        setOpen(true);
        setMessage(data.error || data.message);
        setMessageState("error");
      }
    });
  };

  const handleCancelSubmit = () => {
    // Close the confirmation dialog without submitting
    setConfirmDialogOpen(false);
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <>
      {syncLoading ? (
        <LoadingScreen message="fetching Data ..." />
      ) : (
        <Grid container spacing={2}>
          {newSyncList.map((sync, branchIndex) => (
            <Grid item xs={12} key={branchIndex}>
              <Accordion
                expanded={expandedAccordion === `panel${branchIndex}`}
                onChange={handleChangeAccordion(`panel${branchIndex}`)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${branchIndex}-content`}
                  id={`panel${branchIndex}-header`}
                >
                  <Typography style={{ fontWeight: "bold" }}>
                    {sync.branchName}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {sync.settings.map((setting, settingIndex) => (
                    <div key={settingIndex}>
                      {renderSettingField(setting, branchIndex, settingIndex)}
                    </div>
                  ))}
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
          <Grid item xs={12} sx={{ textAlign: "right", pt: 2, m: 2 }}>
            {loading ? (
              <CircularProgress
                style={{ height: "20px", width: "20px", position: "relative" }}
              />
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                Submit
              </Button>
            )}
          </Grid>
        </Grid>
      )}
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelSubmit}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Submission"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to save these warehouse settings? This action will update the configuration for all selected branches.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSubmit} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmSubmit} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={open}
        onClose={handleCloseSnack}
      >
        <Alert
          onClose={handleCloseSnack}
          severity={messageState}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PL;